import type { MessageTooltipProps } from '@archipro-design/aria';
import { MessageTooltip, useStyles } from '@archipro-design/aria';
import * as S from './OptionLabelWithTooltip.style';
import React from 'react';
import { useIsomorphicLayoutEffect } from 'ahooks';
import { useHydrated } from 'remix-utils';

interface Props {
    label: string;
    tooltipOptions?: Omit<MessageTooltipProps, 'content'>;
}

const OptionLabelWithTooltip = ({ label, tooltipOptions }: Props) => {
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const [tooltipEnabled, setTooltipEnabled] = React.useState(false);
    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    const hydrated = useHydrated();

    const showTooltip = () => {
        if (tooltipEnabled) {
            setTooltipOpen(true);
        }
    };

    const hideTooltip = () => {
        setTooltipOpen(false);
    };

    const { css } = useStyles();

    useIsomorphicLayoutEffect(() => {
        const container = triggerRef.current;
        if (!container || !label) {
            setTooltipEnabled(false);
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }

        const toggleTooltip = () => {
            if (!container) {
                setTooltipEnabled(false);
                return;
            }

            const { scrollWidth, clientWidth, scrollHeight, clientHeight } =
                container;

            if (scrollWidth > clientWidth || scrollHeight > clientHeight) {
                setTooltipEnabled(true);
            } else {
                setTooltipEnabled(false);
            }
        };

        window?.addEventListener('resize', toggleTooltip);
        toggleTooltip();
        return () => {
            window?.removeEventListener('resize', toggleTooltip);
        };
    }, [triggerRef, label]);

    const triggerContent = (
        <div
            className={css(S.TooltipTrigger)}
            ref={triggerRef}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onMouseDown={hideTooltip}
        >
            {label}
        </div>
    );

    return hydrated ? (
        <MessageTooltip
            open={tooltipOpen}
            trigger={triggerContent}
            content={{
                children: label,
                className: css(S.TooltipContent),
                size: 16,
                padding: 16,
            }}
            position="below"
            offset={[4, 7]}
            {...tooltipOptions}
        />
    ) : (
        triggerContent
    );
};
export default OptionLabelWithTooltip;
